import React, { FunctionComponent } from 'react';
import {
  useTranslate,
  Button as RaButton,
  RichTextField as RaRichTextField,
} from 'react-admin';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Typography as MuiTypography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ContractSignatureLaunchModalProps } from './types';

const useStyles = makeStyles({
  item: {
    marginBottom: '16px',
  },
  label: {
    fontSize: '12px',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  actions: {
    justifyContent: 'center',
    marginBottom: '16px',
  },
});

/**
 * Recap shown before launching a contract's e-signature: the PDF sent to
 * Yousign (to open), the contract template, the special terms, the subcontractor (name and email) and the
 * signature type. The signature is launched once confirmed.
 */
const ContractSignatureLaunchModal: FunctionComponent<ContractSignatureLaunchModalProps> =
  (props) => {
    const t = useTranslate();
    const classes = useStyles();
    const { open, contract, loading, handleClose, handleConfirm } = props;

    const item = (label: string, value: React.ReactNode) => (
      <div className={classes.item}>
        <MuiTypography className={classes.label}>{t(label)}</MuiTypography>
        {value || <MuiTypography>-</MuiTypography>}
      </div>
    );

    return (
      <MuiDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <MuiDialogTitle>
          {t('resource.contracts.pdf.launch.label')}
        </MuiDialogTitle>
        <MuiDialogContent>
          {item(
            'resource.contract.section.pdf.title',
            contract.attachment?.public_url && (
              <MuiTypography>
                <a
                  href={contract.attachment.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {contract.attachment.name}
                </a>
              </MuiTypography>
            )
          )}
          {item(
            'resource.purchase_orders.field.contract_template.label',
            contract.contract_template && (
              <MuiTypography>{contract.contract_template}</MuiTypography>
            )
          )}
          {item(
            'resource.purchase_orders.field.special_terms.label',
            contract.special_terms && (
              <RaRichTextField
                record={{
                  id: 'special_terms',
                  special_terms: contract.special_terms,
                }}
                source="special_terms"
              />
            )
          )}
          {item(
            'resource.purchase_orders.field.expert_id.label',
            contract.expert_full_name && (
              <MuiTypography>
                {contract.expert_full_name}
                {contract.expert_email && ` (${contract.expert_email})`}
              </MuiTypography>
            )
          )}
          {item(
            'resource.purchase_orders.field.contract_signature_mode.label',
            <MuiTypography>
              {t(
                `resource.contracts.signature_mode.${
                  contract.signature_mode || 'manual'
                }`
              )}
            </MuiTypography>
          )}
        </MuiDialogContent>
        <MuiDialogActions className={classes.actions}>
          <RaButton
            onClick={handleClose}
            variant="text"
            color="primary"
            label={t('modal.confirm.cancel')}
          />
          <RaButton
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading}
            label={t('resource.contracts.pdf.launch.label')}
          />
        </MuiDialogActions>
      </MuiDialog>
    );
  };

export default ContractSignatureLaunchModal;
